import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';

import { snackbarService } from '/common/snackbarService';
import { IUpdateData, NtfColor } from '/common/SnackBar/customSnackbars/DynamicSnackBar/types';
import { NTF_COLOR, SNACKBAR_AUTO_HIDE_DURATION_MS } from '/common/SnackBar/constants';

interface INotificationOptions {
  msg: string;
  color?: NtfColor;
  buttonText?: string;
  buttonTextColor?: string;
  customAction?: () => void;
  persist?: boolean;
  icon?: ReactNode;
}

interface INotificationController {
  update: (data: IUpdateData, closeStream?: boolean, closeDelay?: number) => void;
  close: () => void;
}

export const useDynamicNotification = () => {
  const activeSubjects = useRef<Subject<IUpdateData>[]>([]);

  useEffect(() => {
    return () => {
      activeSubjects.current.forEach((subj) => {
        if (!subj.closed) {
          subj.next({ forceClose: true });
          subj.complete();
        }
      });
      activeSubjects.current = [];
    };
  }, []);

  const createNotification = useCallback((options: INotificationOptions): INotificationController => {
    const update$ = new Subject<IUpdateData>();
    activeSubjects.current.push(update$);

    snackbarService.custom({
      type: 'dynamic',
      msg: options.msg,
      color: options.color,
      buttonText: options.buttonText,
      buttonTextColor: options.buttonTextColor,
      icon: options.icon,
      customAction: options.customAction,
      persist: options.persist ?? true,
      update$,
    });

    const cleanupStream = () => {
      if (!update$.closed) update$.complete();
      activeSubjects.current = activeSubjects.current.filter((s) => s !== update$);
    };

    const controller: INotificationController = {
      update: (data: IUpdateData, closeStream = false, closeDelay = SNACKBAR_AUTO_HIDE_DURATION_MS) => {
        if (update$.closed) return;

        update$.next(data);

        if (closeStream) {
          if (closeDelay > 0) {
            setTimeout(() => {
              if (!update$.closed) {
                update$.next({ ...data, forceClose: true });
                cleanupStream();
              }
            }, closeDelay);
          } else {
            update$.next({ ...data, forceClose: true });
            cleanupStream();
          }
        }
      },

      close: () => {
        if (!update$.closed) update$.next({ forceClose: true });
        cleanupStream();
      },
    };

    return controller;
  }, []);

  return { createNotification, color: NTF_COLOR };
};
